import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Launch from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';
import GitHubIcon from '@mui/icons-material/GitHub';
import ImageCarousel from '../components/ImageCarousel';
import SEO from '../components/SEO';

// Import logo and screenshots
import logo from '../assets/arutairu/logoApp.png';
import mainScreen from '../assets/arutairu/mainScreen.png';
import flashcards from '../assets/arutairu/flashcards.png';
import reviewLesson from '../assets/arutairu/reviewLesson.png';
import menu from '../assets/arutairu/menu.png';
import customLessons from '../assets/arutairu/customLessonsCreationWizard.png';

function Arutairu() {
  const { t } = useTranslation();
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const features = t('arutairu_page.features.items', { returnObjects: true });
  const learningModes = t('arutairu_page.learningModes.items', { returnObjects: true });
  const mainFeatures = t('arutairu_page.mainFeatures.items', { returnObjects: true });
  
  const stats = [
    { value: '10,000+', label: t('arutairu_page.stats.words') },
    { value: '7,000+', label: t('arutairu_page.stats.downloads') },
    { value: '100%', label: t('arutairu_page.stats.offline') },
    { value: 'N5-N1', label: t('arutairu_page.stats.jlpt') },
  ];

  const screenshots = [
    { src: mainScreen, title: t('arutairu_page.screenshots.mainScreen') },
    { src: flashcards, title: t('arutairu_page.screenshots.flashcards') },
    { src: reviewLesson, title: t('arutairu_page.screenshots.review') },
    { src: menu, title: t('arutairu_page.screenshots.menu') },
    { src: customLessons, title: t('arutairu_page.screenshots.customLessons') },
  ];

  return (
    <>
      <SEO 
        title={`Arutairu - ${t('hero.name')}`}
        description={t('arutairu_page.header.description')}
        type="article"
      />
      <Box sx={{ pb: 10, position: 'relative', overflowX: 'hidden', width: '100%' }}>
        {/* Header Background */}
        <Box
        sx={{
          pt: 12,
          pb: 8,
          width: '100%',
          backgroundColor: '#0e1626',
          backgroundImage: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(14, 22, 38, 0.9) 100%)',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #1f2a3d',
          overflowX: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<ArrowBack />}
              sx={{ color: '#fff' }}
            >
              {t('arutairu_page.back')}
            </Button>
            <Button
              href="https://arutairu.altair-studios.fr/"
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<Launch />}
              sx={{ color: '#4ecdc4' }}
            >
              {t('arutairu_page.visitSite')}
            </Button>
            <Button
              href="https://play.google.com/store/apps/details?id=fr.altairstudios.arutairu"
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<Launch />}
              sx={{ color: '#4ecdc4' }}
            >
              Play Store
            </Button>
            <Button
              href="https://github.com/xenom34/Arutairu"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GitHubIcon />}
              sx={{ color: '#4ecdc4' }}
            >
              GitHub
            </Button>
          </Stack>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Box
              component="img"
              src={logo}
              alt="Arutairu Logo"
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: '20px',
                border: '3px solid #ff6b6b',
                boxShadow: '0 8px 24px rgba(255, 107, 107, 0.3)',
              }}
            />
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  mb: 1,
                  color: '#fff',
                }}
              >
                {t('arutairu_page.title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#a3a3a3',
                  fontWeight: 400,
                }}
              >
                {t('arutairu_page.subtitle')}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip 
              label="Android" 
              sx={{ 
                bgcolor: 'rgba(255, 107, 107, 0.2)', 
                color: '#ff6b6b', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Java" 
              sx={{ 
                bgcolor: 'rgba(78, 205, 196, 0.2)', 
                color: '#4ecdc4', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Open Source" 
              sx={{ 
                bgcolor: 'rgba(255, 215, 61, 0.2)', 
                color: '#ffd93d', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="JLPT" 
              sx={{ 
                bgcolor: 'rgba(255, 107, 107, 0.2)', 
                color: '#ff6b6b', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Offline" 
              sx={{ 
                bgcolor: 'rgba(78, 205, 196, 0.2)', 
                color: '#4ecdc4', 
                borderRadius: '8px',
                fontWeight: 600,
              }} 
            />
          </Stack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: 8 }}>
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {stats.map((stat, idx) => (
            <Grid item xs={6} md={3} key={idx}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  backgroundColor: '#141414',
                  border: '2px solid #2a2a2a',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                    boxShadow: `0 8px 24px ${idx % 3 === 0 ? 'rgba(255, 107, 107, 0.2)' : idx % 3 === 1 ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 215, 61, 0.2)'}`,
                  },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    color: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                    mb: 1,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#a3a3a3',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontSize: '0.75rem',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Introduction */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('arutairu_page.intro.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
              mb: 2,
            }}
          >
            {t('arutairu_page.intro.content')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
            }}
          >
            {t('arutairu_page.intro.opensource')}
          </Typography>
        </Box>

        {/* Technical Architecture */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('arutairu_page.tech.title')}
          </Typography>
          <Box
            sx={{
              p: 3,
              backgroundColor: '#141414',
              border: '2px solid #2a2a2a',
              borderRadius: '12px',
              mb: 3,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#d4d4d4',
                lineHeight: 1.8,
              }}
            >
              {t('arutairu_page.tech.stack')}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 3,
              backgroundColor: '#141414',
              border: '2px solid #2a2a2a',
              borderRadius: '12px',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#d4d4d4',
                lineHeight: 1.8,
              }}
            >
              {t('arutairu_page.tech.features')}
            </Typography>
          </Box>
        </Box>

        {/* What Arutairu Offers */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('arutairu_page.features.title')}
          </Typography>

          <Grid container spacing={3}>
            {features.map((feature, idx) => (
              <Grid item xs={12} key={idx}>
                <Box
                  sx={{
                    p: 4,
                    backgroundColor: '#141414',
                    border: '2px solid #2a2a2a',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                      boxShadow: `0 8px 24px ${idx % 3 === 0 ? 'rgba(255, 107, 107, 0.2)' : idx % 3 === 1 ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 215, 61, 0.2)'}`,
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#d4d4d4',
                      lineHeight: 1.8,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Learning Modes */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('arutairu_page.learningModes.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
              mb: 3,
            }}
          >
            {t('arutairu_page.learningModes.description')}
          </Typography>

          <Grid container spacing={2}>
            {learningModes.map((mode, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: '#141414',
                    border: '2px solid #2a2a2a',
                    borderRadius: '12px',
                    height: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                      boxShadow: `0 8px 24px ${idx % 3 === 0 ? 'rgba(255, 107, 107, 0.2)' : idx % 3 === 1 ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 215, 61, 0.2)'}`,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                    }}
                  >
                    {mode.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#a3a3a3',
                      lineHeight: 1.6,
                    }}
                  >
                    {mode.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Technical Features */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('arutairu_page.mainFeatures.title')}
          </Typography>

          <Grid container spacing={2}>
            {mainFeatures.map((feature, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: '#141414',
                    border: '2px solid #2a2a2a',
                    borderRadius: '12px',
                    height: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                      boxShadow: `0 8px 24px ${idx % 3 === 0 ? 'rgba(255, 107, 107, 0.2)' : idx % 3 === 1 ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 215, 61, 0.2)'}`,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: '#fff',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#a3a3a3',
                      lineHeight: 1.6,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Screenshots */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: '#fff',
            }}
          >
            {t('arutairu_page.screenshots.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
              mb: 4,
            }}
          >
            {t('arutairu_page.screenshots.description')}
          </Typography>

          <Grid container spacing={3}>
            {screenshots.map((screenshot, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '2px solid #2a2a2a',
                    transition: 'all 0.3s ease',
                    cursor: 'zoom-in',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: idx % 3 === 0 ? '#ff6b6b' : idx % 3 === 1 ? '#4ecdc4' : '#ffd93d',
                      boxShadow: `0 12px 32px ${idx % 3 === 0 ? 'rgba(255, 107, 107, 0.3)' : idx % 3 === 1 ? 'rgba(78, 205, 196, 0.3)' : 'rgba(255, 215, 61, 0.3)'}`,
                    },
                  }}
                  onClick={() => {
                    setSelectedImageIndex(idx);
                    setCarouselOpen(true);
                  }}
                >
                  <Box
                    component="img"
                    src={screenshot.src}
                    alt={screenshot.title}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 50%, transparent 100%)',
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#fff',
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      {screenshot.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: '#141414',
            border: '2px solid #2a2a2a',
            borderRadius: '12px',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '6px',
              background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #ffd93d)',
              zIndex: 1,
            },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#fff',
              mb: 2,
            }}
          >
            {t('arutairu_page.cta.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#d4d4d4',
              lineHeight: 1.8,
              mb: 3,
            }}
          >
            {t('arutairu_page.cta.content')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              href="https://play.google.com/store/apps/details?id=fr.altairstudios.arutairu"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#ff6b6b',
                color: '#fff',
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: '#ff8787',
                },
              }}
            >
              {t('arutairu_page.cta.download')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="https://github.com/xenom34/Arutairu"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GitHubIcon />}
              sx={{
                borderColor: '#4ecdc4',
                color: '#4ecdc4',
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#4ecdc4',
                  bgcolor: 'rgba(78, 205, 196, 0.1)',
                },
              }}
            >
              {t('arutairu_page.cta.contribute')}
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Image Carousel */}
      <ImageCarousel
        images={screenshots}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        initialIndex={selectedImageIndex}
      />
    </Box>
    </>
  );
}

export default Arutairu;

